// src/pages/modules/Accounting/PayoutManager/PayoutManager.tsx
// ลูกค้ารอรับเงิน (data_b) → ยืนยันจ่าย → สร้างงวด → ย้ายไป data_c

import React from 'react'
import {
  Alert, Badge, Button, Card, Col,
  Row, Spinner, Table,
} from 'react-bootstrap'
import { AuthContext, dbRead } from '../../../../_services/firebase'
import { getAllCustomers, payoutCustomer, cancelLoanCustomer, PATH } from '../../../../_services/customer.service'
import { calcLoan, formatCurrency, formatDate, toastDanger, toastSuccess } from '../../../../_utils'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import type { CustomerReg, LoanData, InstallmentItem } from '../../../../_types'

// ─── build installment list ───────────────────────────────────
function buildInstallments(loan: LoanData): InstallmentItem[] {
  const items: InstallmentItem[] = []
  for (let i = 1; i <= loan.installmentDays; i++) {
    const item: InstallmentItem = {
      seq:        i,
      dueAmount:  loan.dailyPayment,
      paidAmount: i === 1 ? loan.dailyPayment : 0,  // งวดแรกหักทันที
      status:     i === 1 ? 'paid' : 'pending',
    }
    if (i === 1) item.paidAt = Date.now()
    items.push(item)
  }
  return items
}

// ─── Customer card ────────────────────────────────────────────
function CustomerCard({
  reg, loan, onPay, onCancel, selected, onSelect,
}: {
  reg: CustomerReg
  loan: LoanData | null
  onPay: () => void
  onCancel: () => void
  selected: boolean
  onSelect: () => void
}) {
  const calc = loan
    ? calcLoan(loan.actualAmount, loan.interestRate, loan.installmentDays)
    : null

  return (
    <Card
      className='shadow-sm'
      style={{
        cursor: 'pointer',
        borderColor: selected ? '#ffc107' : '#e0e0e0',
        borderWidth: selected ? 2 : 1,
      }}
      onClick={onSelect}
    >
      <Card.Body>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{reg.personal.fullName}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{formatDate(reg.meta.updatedAt)}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{reg.key}</div>
          </div>
          <Badge bg='warning' text='dark' style={{ fontSize: 10 }}>waiting</Badge>
        </div>

        {loan && calc ? (
          <div style={{ fontSize: 13 }}>
            <div>ยอดกู้จริง: <strong>{formatCurrency(loan.actualAmount)}</strong> กีบ</div>
            <div>จำนวนงวด: <strong>{loan.installmentDays} วัน</strong></div>
            <div>วงเงินที่ขอ: <strong>{formatCurrency(loan.wantedAmount)}</strong> กีบ</div>
            <div>โทร: <strong>{reg.personal.phone || '-'}</strong></div>
            <div>โซน: <strong>{Array.isArray(reg.loan.zone) ? reg.loan.zone.join('+') : reg.loan.zone}</strong></div>
            <div style={{ fontSize: 11, color: '#888' }}>{reg.personal.address || '-'}</div>
          </div>
        ) : (
          <div className='text-muted' style={{ fontSize: 12 }}>ไม่พบข้อมูลสินเชื่อ</div>
        )}

        <div className='d-flex gap-2 mt-3'>
          <Button
            variant='primary'
            size='sm'
            className='flex-fill'
            onClick={(e) => { e.stopPropagation(); onPay() }}
            disabled={!loan}
          >
            <i className='fa-solid fa-money-bill-transfer me-1' />จ่ายเงิน
          </Button>
          <Button
            variant='outline-danger'
            size='sm'
            onClick={(e) => { e.stopPropagation(); onCancel() }}
          >
            <i className='fa-solid fa-xmark me-1' />ยกเลิก
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

// ─── PayoutManager ────────────────────────────────────────────
export default function PayoutManager() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [loading,     setLoading]     = React.useState(true)
  const [customers,   setCustomers]   = React.useState<CustomerReg[]>([])
  const [loans,       setLoans]       = React.useState<Record<string, LoanData>>({})
  const [viewMode,    setViewMode]    = React.useState<'Grid' | 'List'>('Grid')

  const [selected,    setSelected]    = React.useState<CustomerReg | null>(null)
  const [confirmShow, setConfirmShow] = React.useState(false)
  const [saving,      setSaving]      = React.useState(false)
  const [cancelTarget, setCancelTarget] = React.useState<CustomerReg | null>(null)
  const [cancelReason, setCancelReason] = React.useState('')
  const [cancelShow,   setCancelShow]   = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const list = await getAllCustomers('loan')
      setCustomers(list.sort((a, b) => b.meta.updatedAt - a.meta.updatedAt))

      // load loan data for each customer
      const loanMap: Record<string, LoanData> = {}
      await Promise.all(
        list.map(async (c) => {
          const l = await dbRead<LoanData>(PATH.loan(c.key))
          if (l) loanMap[c.key] = l
        })
      )
      setLoans(loanMap)
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { loadData() }, [loadData])

  const handleCancel = async () => {
    if (!cancelTarget) return
    setSaving(true)
    try {
      await cancelLoanCustomer(uid, cancelTarget.key, cancelTarget, cancelReason || 'ยกเลิก')
      toastSuccess(`ยกเลิกสินเชื่อ ${cancelTarget.personal.fullName} → data_d แล้ว`)
      setCancelTarget(null)
      setCancelReason('')
      setCancelShow(false)
      await loadData()
    } catch (e: any) {
      toastDanger(e?.message || 'ยกเลิกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const handlePay = async () => {
    if (!selected) return
    const loan = loans[selected.key]
    if (!loan) { toastDanger('ไม่พบข้อมูลสินเชื่อ'); return }

    setSaving(true)
    try {
      const installments = buildInstallments(loan)
      await payoutCustomer(uid, selected.key, selected, loan, installments)
      toastSuccess(`จ่ายเงินให้ ${selected.personal.fullName} เรียบร้อย`)
      setSelected(null)
      await loadData()
    } catch (e: any) {
      toastDanger(e?.message || 'จ่ายเงินไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  // ─── Selected loan preview ────────────────────────────────────
  const selLoan = selected ? loans[selected.key] : null
  const selCalc = selLoan
    ? calcLoan(selLoan.actualAmount, selLoan.interestRate, selLoan.installmentDays)
    : null

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <Spinner animation='border' variant='warning' />
      </div>
    )
  }

  return (
    <div className='p-3'>
      {/* Cancel Modal */}
      {cancelShow && cancelTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
          zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setCancelShow(false)}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 420, maxWidth: '95vw' }}
               onClick={e => e.stopPropagation()}>
            <h6 style={{ marginBottom: 12 }}>⚠️ ยกเลิก/ปฏิเสธสินเชื่อ</h6>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              ลูกค้า: <strong>{cancelTarget.personal.fullName}</strong><br/>
              ระบบจะย้ายไปยัง <strong>data_d (ปิดบัญชี)</strong>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>เหตุผล</label>
              <textarea
                className='form-control form-control-sm mt-1'
                rows={2}
                placeholder='ระบุเหตุผล...'
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
              />
            </div>
            <div className='d-flex gap-2 justify-content-end'>
              <button className='btn btn-sm btn-outline-secondary'
                onClick={() => { setCancelShow(false); setCancelTarget(null); setCancelReason('') }}>
                ยกเลิก
              </button>
              <button className='btn btn-sm btn-danger' disabled={saving} onClick={handleCancel}>
                {saving ? 'กำลังบันทึก...' : 'ยืนยันยกเลิกสินเชื่อ'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        show={confirmShow}
        title='ยืนยันการจ่ายเงิน'
        message={
          selected && selCalc
            ? `จ่ายเงิน ${formatCurrency(selCalc.payoutAmount)} กีบ ให้ ${selected.personal.fullName} (หักงวดแรก ${formatCurrency(selCalc.dailyPayment)} กีบแล้ว)`
            : 'ยืนยันการจ่ายเงิน?'
        }
        confirmLabel={saving ? 'กำลังบันทึก...' : 'ยืนยันจ่ายเงิน'}
        variant='success'
        onConfirm={handlePay}
        onCancel={() => setConfirmShow(false)}
      />

      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>ลูกค้ารอรับเงิน</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>
            มี {customers.length} รายการ
          </div>
        </div>
        <div className='d-flex gap-2'>
          <Button
            size='sm' variant={viewMode === 'Grid' ? 'warning' : 'outline-secondary'}
            onClick={() => setViewMode('Grid')}
          >Grid</Button>
          <Button
            size='sm' variant={viewMode === 'List' ? 'warning' : 'outline-secondary'}
            onClick={() => setViewMode('List')}
          >List</Button>
          <Button size='sm' variant='outline-secondary' onClick={loadData}>
            <i className='fa-solid fa-rotate-right' />
          </Button>
        </div>
      </div>

      {customers.length === 0 && (
        <Alert variant='info' style={{ fontSize: 13 }}>ไม่มีลูกค้ารอรับเงิน</Alert>
      )}

      {/* Confirm detail panel */}
      {/*selected && selCalc && (
        <Card className='mb-3 shadow-sm' style={{ borderColor: '#ffc107', borderWidth: 2 }}>
          <Card.Header style={{ backgroundColor: '#fffbf0', fontWeight: 700, fontSize: 13 }}>
            <i className='fa-solid fa-money-bill-wave me-2 text-warning' />
            ยืนยันการจ่ายเงิน: {selected.personal.fullName}
          </Card.Header>
          <Card.Body>
            <Row className='g-3'>
              <Col xs={12} md={6}>
                <Table size='sm' style={{ fontSize: 13 }}>
                  <tbody>
                    <tr><td style={{ fontWeight: 600 }}>ยอดกู้จริง</td><td className='text-end'>{formatCurrency(selLoan!.actualAmount)} กีบ</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>ดอกเบี้ย {selLoan!.interestRate}%</td><td className='text-end'>{formatCurrency(selCalc.interestAmount)} กีบ</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>ยอดรวม</td><td className='text-end'>{formatCurrency(selCalc.totalWithInterest)} กีบ</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>ยอดส่ง/{selLoan!.installmentDays} วัน</td><td className='text-end'>{formatCurrency(selCalc.dailyPayment)} กีบ</td></tr>
                    <tr style={{ backgroundColor: '#e8f5e9' }}>
                      <td style={{ fontWeight: 700 }}>หักงวดแรก → โอนให้ลูกค้า</td>
                      <td className='text-end' style={{ fontWeight: 700, color: '#2e7d32' }}>
                        {formatCurrency(selCalc.payoutAmount)} กีบ
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col xs={12} md={6}>
                <div style={{ fontSize: 13 }}>
                  <div><strong>โซน:</strong> {selLoan!.zone}</div>
                  <div><strong>วัตถุประสงค์:</strong> {selLoan!.purpose || '-'}</div>
                  <div><strong>งวด 1 (หักทันที):</strong> {formatCurrency(selCalc.dailyPayment)} กีบ</div>
                  <div className='text-muted' style={{ fontSize: 11, marginTop: 8 }}>
                    หลังยืนยัน: ระบบจะสร้าง {selLoan!.installmentDays} งวด และย้ายข้อมูลไปยัง data_c
                  </div>
                </div>
                <div className='d-flex gap-2 mt-3'>
                  <Button variant='outline-secondary' size='sm' onClick={() => setSelected(null)}>ยกเลิก</Button>
                  <Button variant='success' size='sm' onClick={() => setConfirmShow(true)} disabled={saving}>
                    <i className='fa-solid fa-check me-1' />ยืนยันจ่ายเงิน
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )*/}

      {/* Grid view */}
      {viewMode === 'Grid' && (
        <Row className='g-3'>
          {customers.map((c) => (
            <Col key={c.key} xs={12} md={6} lg={4}>
              <CustomerCard
                reg={c}
                loan={loans[c.key] || null}
                selected={selected?.key === c.key}
                onSelect={() => setSelected(c)}
                onPay={() => { setSelected(c); setConfirmShow(true) }}
                onCancel={() => { setCancelTarget(c); setCancelShow(true) }}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* List view */}
      {viewMode === 'List' && (
        <Table size='sm' bordered hover style={{ fontSize: 13 }}>
          <thead className='table-light'>
            <tr>
              <th>ชื่อ</th><th>โทร</th><th>ยอดกู้</th><th>งวด</th><th>โซน</th><th>วันที่</th><th></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => {
              const l = loans[c.key]
              return (
                <tr key={c.key}>
                  <td>{c.personal.fullName}</td>
                  <td>{c.personal.phone || '-'}</td>
                  <td>{l ? formatCurrency(l.actualAmount) : '-'}</td>
                  <td>{l ? `${l.installmentDays} วัน` : '-'}</td>
                  <td>{l?.zone || '-'}</td>
                  <td>{formatDate(c.meta.updatedAt)}</td>
                  <td>
                    <div className='d-flex gap-1'>
                      <Button
                        size='sm' variant='primary'
                        onClick={() => { setSelected(c); setConfirmShow(true) }}
                        disabled={!l}
                      >
                        จ่ายเงิน
                      </Button>
                      <Button
                        size='sm' variant='outline-danger'
                        onClick={() => { setCancelTarget(c); setCancelShow(true) }}
                      >
                        ยกเลิก
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </div>
  )
}